// src/components/EventActions/EventActions.jsx
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import useCustomMutation from "../../../Hooks/useCustomMutation";
import SmBtn from "../../Buttons/SmBtn";
import ModalDelete from "../../Modal/ModalDelete";
import DeleteForm from "../../EventSetUp/EventBranding/DeleteEvent";
import { 
  Modal, 
  ModalHeader, 
  ModalBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from "reactstrap";
import { withTranslation } from "react-i18next";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CiLink, CiMenuKebab } from "react-icons/ci";
import { 
  ArrowRight, 
  Share2, 
  Send,
  Eye, 
  Trash2, 
  XCircle 
} from "lucide-react";

const EventActions = (props) => {
  const Navigate = useNavigate();
  const eventID = props?.eventID;
  const token = props?.token;
  const params = useParams();
  const eventName = params?.eventName;
  const [isToggleDelete, setIsToggleDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isToggleShare, setIsToggleShare] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const userEmail = props?.userEmail;
  const getEventDetails = props?.getEventDetails;
  const state = getEventDetails?.data?.state;

  // Safely derive event share URL
  const hasEventData = !!eventID && !!eventName;
  const safeEventName = eventName ? eventName.replace(/ /g, "-") : "";
  const frontendBase =
    process.env.React_APP_Frontend || `${window.location.origin}/`;
  const eventUrl = hasEventData
    ? `${frontendBase}${safeEventName}/p/event-landing/${eventID}`
    : "";
  
  const dropdownRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMoreOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggleDelete = () => {
    setIsToggleDelete(!isToggleDelete);
    setIsMoreOpen(false);
  };
  
  const key = ["Events", userEmail];
  const keyPublish = ["EventDetails", eventID];

  const mutationDelete = useCustomMutation(key);
  const mutationPublish = useCustomMutation(keyPublish);

  const DeleteEvent = () => {
    const Method = "DELETE",
      url = `events/${eventID}`,
      isJSON = null;
    var raw = null;
    const data = {
      endPoint: url,
      bodyContent: raw,
      Method: Method,
      displayMessage: "Event successfully deleted",
      token: token,
      isJSON: isJSON,
    };
    mutationDelete.mutate(data);
  };
  
  useEffect(() => {
    if (mutationDelete?.isSuccess) {
      Navigate("/dashboard");
      setIsMoreOpen(false);
    }
  }, [mutationDelete?.isSuccess]);

  const handleEventState = (eventStatus) => {
    const Method = "PATCH",
      url = `events/${eventID}`,
      isJSON = true;
    var formdata = new FormData();
    if (eventStatus) formdata.append("state", eventStatus);
    const data = {
      endPoint: url,
      bodyContent: formdata,
      Method: Method,
      displayMessage: "Event Status successfully updated",
      token: token,
      isJSON: isJSON,
    };
    mutationPublish.mutate(data);
    setIsMoreOpen(false);
  };
  
  const handleShareEvent = () => {
    setIsToggleShare(!isToggleShare);
    setIsMoreOpen(false);
  };

  const handleEnterEvent = () => {
    if (getEventDetails?.data?.eventType?.includes("Webinar")) {
      Navigate(`/${eventName}/w/${eventID}`);
    } else if (getEventDetails?.data?.eventType?.includes("Conference")) {
      Navigate(`/${eventName}/conference/${eventID}`);
    } else {
      Navigate(`/${eventName}/exhibition/${eventID}`);
    }
    setIsMoreOpen(false);
  };

  // ✅ REMOVED: handlePreviewEvent function no longer needed

  const toggleMoreMenu = () => {
    setIsMoreOpen(!isMoreOpen);
  };

  return (
    <React.Fragment>
      {/* DESKTOP VIEW - All buttons horizontal */}
      <div 
        className="event-actions-desktop"
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
          flexWrap: 'wrap',
          margin: '10px 0'
        }}
      >
        <SmBtn
          Title="Share Event"
          BackgroundColor="#fff"
          ColorText="#0ac074"
          BorderColor="#0ac074"
          borderRadius={25}
          handleOnclick={handleShareEvent}
        />
        {state === "Not Published" ? (
          <SmBtn
            Title="Publish Event"
            BackgroundColor="#fff"
            ColorText="#0ac074"
            BorderColor="#0ac074"
            borderRadius={25}
            handleOnclick={() => handleEventState("Published")}
            pending={mutationPublish?.isLoading}
          />
        ) : null}

        {state === "Published" ? (
          <SmBtn
            Title="Unpublish Event"
            BackgroundColor="#fff"
            ColorText="#ff0000"
            BorderColor="#ff0000"
            borderRadius={25}
            handleOnclick={() => handleEventState("Not Published")}
            pending={mutationPublish?.isLoading}
          />
        ) : null}

        <SmBtn
          Title="Enter Event"
          BackgroundColor="white"
          ColorText="#f68a20"
          BorderColor="#f68a20"
          borderRadius={25}
          handleOnclick={handleEnterEvent}
          pending={false}
        />
        {/* ✅ REMOVED: Preview Event Button */}
        <SmBtn
          Title="Delete Event"
          BackgroundColor="#fff"
          ColorText="#ff0000"
          BorderColor="#ff0000"
          borderRadius={25}
          handleOnclick={handleToggleDelete}
        />
      </div>

      {/* MOBILE VIEW - Kebab menu ONLY (Enter button inside menu) */}
      <div 
        className="event-actions-mobile"
        ref={dropdownRef}
        style={{
          display: 'none',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0',
          margin: '0',
          padding: '0',
          position: 'relative',
          flexWrap: 'nowrap',
          width: '100%',
          zIndex: 1000
        }}
      >
        {/* More Actions Button (Kebab Menu) - Centered */}
        <button
          onClick={toggleMoreMenu}
          aria-label="More actions"
          aria-expanded={isMoreOpen}
          style={{
            background: isMoreOpen ? 'rgba(246, 138, 32, 0.15)' : 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            transition: 'all 0.2s ease',
            flexShrink: 0,
            margin: '0 auto'
          }}
          onMouseEnter={(e) => {
            if (!isMoreOpen) {
              e.currentTarget.style.backgroundColor = 'rgba(246, 138, 32, 0.1)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isMoreOpen) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          <CiMenuKebab size={18} color="#f68a20" />
        </button>
        
        {/* Dropdown Menu */}
        {isMoreOpen && (
          <div 
            className="more-actions-menu show"
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: 'calc(100% + 8px)',
              minWidth: '180px',
              backgroundColor: '#616467',
              border: '1px solid #334155',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              zIndex: 9999,
              padding: '6px 0',
              animation: 'slideDown 0.2s ease-out'
            }}
          >
            {/* ✅ ENTER EVENT - Now inside the menu */}
            <div
              onClick={() => {
                handleEnterEvent();
                setIsMoreOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                color: '#f68a20',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                borderBottom: '1px solid #334155',
                marginBottom: '4px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#334155';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <ArrowRight size={12} />
              <span>Enter Event</span>
            </div>
            
            <div
              onClick={() => {
                handleShareEvent();
                setIsMoreOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                color: '#e2e8f0',
                cursor: 'pointer',
                fontSize: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#334155';
                e.currentTarget.style.color = '#f68a20';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#e2e8f0';
              }}
            >
              <Share2 size={12} />
              <span>Share</span>
            </div>
            
            {state === "Not Published" ? (
              <div
                onClick={() => {
                  handleEventState("Published");
                  setIsMoreOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  color: '#0ac074',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#334155';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Send size={12} />
                <span>Publish</span>
              </div>
            ) : (
              <div
                onClick={() => {
                  handleEventState("Not Published");
                  setIsMoreOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  color: '#ff0000',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#334155';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <XCircle size={12} />
                <span>Unpublish</span>
              </div>
            )}
            
            {/* ✅ REMOVED: Preview menu item */}
            
            <div
              onClick={() => {
                handleToggleDelete();
                setIsMoreOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                color: '#ff0000',
                cursor: 'pointer',
                fontSize: '12px',
                borderTop: '1px solid #334155',
                marginTop: '4px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#334155';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Trash2 size={12} />
              <span>Delete</span>
            </div>
          </div>
        )}
      </div>

      {/* CSS for responsive behavior */}
      <style>{`
        @media (max-width: 768px) {
          .event-actions-desktop {
            display: none !important;
          }
          .event-actions-mobile {
            display: flex !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: center !important;
          }
        }
        
        @media (min-width: 769px) {
          .event-actions-desktop {
            display: flex !important;
          }
          .event-actions-mobile {
            display: none !important;
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>

      {/* Delete Modal */}
      <ModalDelete
        size="md"
        isOpen={isToggleDelete}
        toggle={() => {
          setIsToggleDelete(false);
          setIsMoreOpen(false);
        }}
        title="Delete Event"
        body={
          <DeleteForm
            setIsToggleDelete={setIsToggleDelete}
            DeleteEvent={DeleteEvent}
            pending={mutationDelete?.isLoading}
          />
        }
      />
      
      
<Modal 
  size="md" 
  isOpen={isToggleShare} 
  toggle={() => {
    handleShareEvent();
    setIsMoreOpen(false);
  }}
  className="share-modal"
  centered
>
  <ModalHeader 
    toggle={() => {
      handleShareEvent();
      setIsMoreOpen(false);
    }}
    style={{
      borderBottom: '2px solid #f68a20',
      paddingBottom: '15px'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <Share2 size={20} color="#f68a20" />
      <span style={{ fontWeight: '700', color: '#1a1a1a' }}>Share Event</span>
    </div>
  </ModalHeader>
  <ModalBody style={{ padding: '24px' }}>
    {hasEventData ? (
      <>
        {/* URL Display Box */}
        <div 
          style={{
            backgroundColor: '#f8f9fa',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            position: 'relative'
          }}
        >
          <label 
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: '600',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px'
            }}
          >
            Event Link
          </label>
          
          {/* URL Text - More Visible */}
          <div 
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '13px',
              color: '#1a1a1a',
              fontWeight: '500',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              lineHeight: '1.5',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
            }}
          >
            {eventUrl}
          </div>
        </div>

        {/* Copy Button - Large and Prominent */}
        <CopyToClipboard text={eventUrl} onCopy={() => setCopied(true)}>
          <button
            type="button"
            style={{
              width: '100%',
              backgroundColor: copied ? '#10b981' : '#f68a20',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              padding: '14px 20px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'all 0.3s ease',
              boxShadow: copied 
                ? '0 4px 12px rgba(16, 185, 129, 0.3)' 
                : '0 4px 12px rgba(246, 138, 32, 0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
            onMouseEnter={(e) => {
              if (!copied) {
                e.currentTarget.style.backgroundColor = '#fb923c';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(246, 138, 32, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!copied) {
                e.currentTarget.style.backgroundColor = '#f68a20';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(246, 138, 32, 0.3)';
              }
            }}
          >
            {copied ? (
              <>
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="3"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Copied to Clipboard!</span>
              </>
            ) : (
              <>
                <CiLink size={20} />
                <span>Copy Link</span>
              </>
            )}
          </button>
        </CopyToClipboard>

        {/* Success Message - More Prominent */}
        {copied && (
          <div 
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#d1fae5',
              border: '2px solid #10b981',
              borderRadius: '8px',
              textAlign: 'center',
              animation: 'slideIn 0.3s ease'
            }}
          >
            <div style={{ color: '#059669', fontWeight: '700', fontSize: '14px' }}>
              ✓ Link copied successfully!
            </div>
            <div style={{ color: '#059669', fontSize: '12px', marginTop: '4px' }}>
              You can now paste it anywhere
            </div>
          </div>
        )}

        {/* Quick Share Options */}
        <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '12px', fontWeight: '600' }}>
            Or share via:
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => {
                window.open(`https://wa.me/?text=${encodeURIComponent(eventUrl)}`, '_blank');
              }}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#25D366',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#128C7E';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#25D366';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp
            </button>
            
            <button
              onClick={() => {
                window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('Check out this event!')}&url=${encodeURIComponent(eventUrl)}`, '_blank');
              }}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#1DA1F2',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1A8CD8';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1DA1F2';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
              Twitter
            </button>
            
            <button
              onClick={() => {
                window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(eventUrl)}`, '_blank');
              }}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#0A66C2',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#004182';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#0A66C2';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              LinkedIn
            </button>
          </div>
        </div>
      </>
    ) : (
      <div 
        style={{
          padding: '30px',
          textAlign: 'center',
          backgroundColor: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '12px'
        }}
      >
        <div style={{ color: '#92400e', fontSize: '14px', fontWeight: '600' }}>
          ⚠ Event link unavailable
        </div>
        <div style={{ color: '#92400e', fontSize: '12px', marginTop: '8px' }}>
          Please ensure the event is properly configured
        </div>
      </div>
    )}
  </ModalBody>
</Modal>

{/* Add animation styles */}
<style>{`
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`}</style>
    </React.Fragment>
  );
};

EventActions.propTypes = {
  t: PropTypes.any,
  eventID: PropTypes.string,
  token: PropTypes.string,
  userEmail: PropTypes.string,
  getEventDetails: PropTypes.object,
};

export default withTranslation()(EventActions);